import React, { useState } from 'react';
import {
  Typography,
  Container,
  Box,
  Button,
  Avatar,
  Modal,
  Grid,
  Card,
  CardContent,
  CardActions,
  Fade,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import {
  LocalHospital,
  Psychology,
  FitnessCenter,
  Healing,
  Restaurant,
  PregnantWoman,
  SelfImprovement,
  InfoOutlined,
  LaunchOutlined,
  EmailOutlined,
  CloseOutlined
} from '@mui/icons-material';
import room01 from './assets/room01.jpg';
import room02 from './assets/room02.jpg';
import room03 from './assets/room03.jpg';
import room04 from './assets/room04.jpg';
import room05 from './assets/room05.jpg';
import room06 from './assets/room06.jpg';
import room07 from './assets/room07.jpg';
import servicesBg from './assets/services_bg.jpg';

const doctors = [
  {
    name: "Dr Laeeqah van der Schyff",
    specialization: "Medical and Paediatric GP",
    description:
      "Dr van der Schyff is dedicated General Practicioner with a special interest in Paediatrics and Family Medicine. Whether you're booking your child's first check-up or managing your own long-term wellness, you can expect care rooted in compassion, trust and a deep understanding of every stage of life.\n\nIf you're looking for a doctor who strives for medical excellence and heartfelt care, Dr Laeeqah van der Schyff is here to walk every step of the health journey with you and your family.",
    image: room01,
    website: "https://www.drvanderschyff.co.za/",
    bookingEmail: "reception@constantiamed.co.za",
    icon: LocalHospital
  },
  {
    name: "Malcolm Freedman",
    specialization: "Orthotist & Prosthetist",
    description:
      "Malcolm specialises in lower limb biomechanical gait analysis, orthotic solutions for osteoarthritis of the knee, sport injuries and rehabilitation, as well as conservative management of scoliosis and acute spinal injuries.\n\nHis patients have immediate access to the very best in sports, medical and lifestyle support products. Assessment, casting and manufacturing of prosthetic devices is done in his lab where he works, together with the amputees, to achieve their goals. Malcolm's passion is to preserve and improve the quality of life of all his patients.",
    image: room02,
    website: "https://www.orthopro.co.za",
    bookingEmail: "reception@constantiamed.co.za",
    icon: FitnessCenter
  },
  {
    name: "Dr Amina Guffar",
    specialization: "Women's Health and Medical Aesthetics",
    description:
      "Dr Amina is a medical practitioner with a special interest in Female Health and Aesthetic Medicine. Her consultation incorporates evidence-based modern medicine along with integrative approaches.\n\nThe aim of her practice is to empower and enhance the lives of women through beauty and overall wellbeing -physically, mentally & aesthetically.",
    image: room03,
    website: "https://www.draminaguffar.co.za",
    bookingEmail: "reception@constantiamed.co.za",
    icon: Psychology
  },
  {
    name: "IV Medical Care",
    specialization: "Dr Yusuf Arieff: Vitamin & Iron Infusion",
    description:
      "Dr Yusuf Arieff is a caring and highly-experienced General Practitioner with a focus on IV vitamin and iron infusions tailored to individual needs. He's realised the great impact iron and vitamins have to a better quality, stress-free and energetic life. With experience in iron infusions, he ventured into unique formulations of vitamin infusions which are exclusively available at Constantia MediSuites, including the pioneering Anti-Aging NAD Infusion.\n\nDr Arieff has successfully tailored infusions for women in all stages of life, sports enthusiasts, and individuals with different health and wellness goals. Iron infusions are safely given without having the need of being admitted to a hospital. He'll also be offering biologics in the near future.",
    image: room05,
    website: null,
    bookingEmail: "reception@constantiamed.co.za",
    icon: Healing
  },
  {
    name: "Tracy Ugarchund & Associate Dietitians",
    specialization: "Registered Dietitian",
    description:
      "Tracy Ugarchund has many years of experience in almost all aspects of nutrition and diet including weight management, diabetes, oncology and critical care. She is driven to help people see that food can be enjoyed even when it's healthy.\n\nHer tailored treatment plans aim to empower her clients with the ability to plan, prepare and cook their own healthy food and help overcome chronic lifestyle diseases. She helps clients establish personalised dietary & health goals, while respecting cultural, budget & lifestyle needs for holistic nutrition.",
    image: room04,
    website: "https://tracyugarchund.co.za",
    bookingEmail: "reception@constantiamed.co.za",
    icon: Restaurant
  },
  {
    name: "Dr Marlize Lerm",
    specialization: "Obstetrician and Gynaecologist (OBGYN)",
    description:
      "Dr Marlize Lerm provides a supportive space in which she treats a vast array of gynaecological conditions. She has a keen interest in maternal mental health and believes screening for disorders forms an integrative part of obstetric care. With her skilled technique and only the most advanced technology, she performs diagnostic procedures in order to source the best possible treatment and management of complex gynaecological and obstetric conditions.",
    image: room06,
    website: "https://gynaecologistcapetown.co.za/dr-marlize-lerm-obgyn",
    bookingEmail: "reception@constantiamed.co.za",
    icon: PregnantWoman
  },
  {
    name: "Jess Boardman Physiotherapy",
    specialization: "Physiotherapist",
    description:
      "Jessica Boardman completed her studies at University of Cape Town, and obtained additional qualifications in Pilates (APPI: Australian Physiotherapy & Pilates Institute), Dry Needling and Orthopaedic Manipulative Therapy (OMT). She’s been working in private practice since 2018, interning with the Stormers Rugby and UCT Ikey Tigers, and worked in various Cape Town hospitals and step-down facilities.\n\nShe strongly believes staying up-to-date with the latest research is essential for patient outcomes. Her work interests include breast cancer post-op management, musculoskeletal conditions and sports injuries.",
    image: room07,
    website: "https://www.linkedin.com/in/jessica-boardman-2a99b7149",
    bookingEmail: "reception@constantiamed.co.za",
    icon: SelfImprovement
  }
];

// Styled Components
const StyledCard = styled(Card)(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'transparent !important',
  border: 'none !important',
  boxShadow: 'none !important',
  transition: 'none !important',
  transform: 'none !important',
  '&:hover': {
    background: 'transparent !important',
    boxShadow: 'none !important',
    transform: 'none !important',
    border: 'none !important',
  },
  '&:focus': {
    background: 'transparent !important',
    boxShadow: 'none !important',
    transform: 'none !important',
    border: 'none !important',
  },
  '&:active': {
    background: 'transparent !important',
    boxShadow: 'none !important',
    transform: 'none !important',
    border: 'none !important',
  },
  // ABSOLUTELY NO animations, transitions, or hover effects on the card
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.primary.main}`,
  boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.secondary.main}`,
    opacity: 0,
    transform: 'scale(0.8)',
    transition: 'all 0.3s ease',
  },
  '&:hover': {
    borderColor: theme.palette.secondary.main,
    '&::before': {
      opacity: 1,
    },
    '&::after': {
      opacity: 0.6,
      transform: 'scale(1)',
      animation: 'pulse 2s infinite',
    },
    '& img': {
      filter: 'brightness(1.1) contrast(1.05)',
    },
  },
  '&:active': {
    transform: 'translateY(2px)',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.6,
    },
    '50%': {
      transform: 'scale(1.05)',
      opacity: 0.3,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 0.6,
    },
  },
}));



const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const ModalContent = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 20,
  padding: theme.spacing(4),
  maxWidth: 600,
  maxHeight: '90vh',
  overflow: 'auto',
  position: 'relative',
  boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.divider}`,
}));

const DoctorCard = ({ doctor }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <StyledCard>
        <CardContent sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          pt: 3,
          pb: 2,
          flex: 1,
          background: 'transparent',
          '&:hover': {
            background: 'transparent',
          }
        }}>
          <Tooltip title="Click for more information" arrow placement="top">
            <StyledAvatar
              alt={doctor.name}
              src={doctor.image}
              onClick={handleOpen}
              sx={{ mb: 2 }}
            />
          </Tooltip>

          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 1,
              lineHeight: 1.3
            }}
          >
            {doctor.name}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 500,
              mb: 2,
              fontSize: '0.95rem'
            }}
          >
            {doctor.specialization}
          </Typography>
        </CardContent>

        <CardActions sx={{
          justifyContent: 'center',
          pb: 2,
          background: 'transparent',
          '&:hover': {
            background: 'transparent',
          }
        }}>
          <Button
            variant="outlined"
            onClick={handleOpen}
            startIcon={<InfoOutlined />}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              borderWidth: '2px',
              borderRadius: '12px',
              padding: '10px 24px',
              fontWeight: 600,
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, transparent, ${theme.palette.secondary.main}20, transparent)`,
                transition: 'left 0.6s ease',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                borderRadius: '14px',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                backgroundSize: '200% 200%',
                opacity: 0,
                zIndex: -1,
                transition: 'opacity 0.3s ease',
                animation: 'none',
              },
              '&:hover': {
                borderColor: theme.palette.secondary.main,
                color: 'white',
                backgroundColor: theme.palette.secondary.main,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${theme.palette.secondary.main}30`,
                '&::before': {
                  left: '100%',
                },
                '&::after': {
                  opacity: 1,
                  animation: 'borderGlow 2s ease-in-out infinite',
                },
                '& .MuiSvgIcon-root': {
                  transform: 'rotate(360deg)',
                  color: 'white',
                },
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              '& .MuiSvgIcon-root': {
                transition: 'all 0.4s ease',
              },
              '@keyframes borderGlow': {
                '0%': {
                  backgroundPosition: '0% 50%',
                },
                '50%': {
                  backgroundPosition: '100% 50%',
                },
                '100%': {
                  backgroundPosition: '0% 50%',
                },
              },
            }}
          >
            More Info
          </Button>
        </CardActions>
      </StyledCard>

      <StyledModal
        open={open}
        onClose={handleClose}
        aria-labelledby="doctor-modal-title"
        aria-describedby="doctor-modal-description"
      >
        <Fade in={open}>
          <ModalContent>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: theme.palette.text.secondary
              }}
            >
              <CloseOutlined />
            </IconButton>

            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  mb: 1
                }}
              >
                {doctor.name}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 500
                }}
              >
                {doctor.specialization}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <StyledAvatar
                alt={doctor.name}
                src={doctor.image}
                sx={{ width: 200, height: 200 }}
              />
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.7,
                mb: 3
              }}
            >
              {doctor.description.split('\n\n').map((paragraph, index) => (
                <Box key={index} mb={2}>
                  {paragraph}
                </Box>
              ))}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {doctor.website && (
                <Button
                  href={doctor.website}
                  variant="contained"
                  target="_blank"
                  rel="noopener"
                  startIcon={<LaunchOutlined />}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontWeight: 600,
                    textTransform: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: 'left 0.6s ease',
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                      '&::before': {
                        left: '100%',
                      },
                      '& .MuiSvgIcon-root': {
                        transform: 'rotate(360deg)',
                      },
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    '& .MuiSvgIcon-root': {
                      transition: 'all 0.4s ease',
                    },
                  }}
                >
                  Visit Website
                </Button>
              )}
              <Button
                href={`mailto:${doctor.bookingEmail}`}
                variant="outlined"
                startIcon={<EmailOutlined />}
                sx={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                  borderWidth: '2px',
                  borderRadius: '12px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: `linear-gradient(90deg, transparent, ${theme.palette.secondary.main}20, transparent)`,
                    transition: 'left 0.6s ease',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '14px',
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundSize: '200% 200%',
                    opacity: 0,
                    zIndex: -1,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    color: 'white',
                    backgroundColor: theme.palette.secondary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${theme.palette.secondary.main}30`,
                    '&::before': {
                      left: '100%',
                    },
                    '&::after': {
                      opacity: 1,
                      animation: 'borderGlow 2s ease-in-out infinite',
                    },
                    '& .MuiSvgIcon-root': {
                      transform: 'rotate(360deg)',
                      color: 'white',
                    },
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '& .MuiSvgIcon-root': {
                    transition: 'all 0.4s ease',
                  },
                  '@keyframes borderGlow': {
                    '0%': {
                      backgroundPosition: '0% 50%',
                    },
                    '50%': {
                      backgroundPosition: '100% 50%',
                    },
                    '100%': {
                      backgroundPosition: '0% 50%',
                    },
                  },
                }}
              >
                Book Appointment
              </Button>
            </Box>
          </ModalContent>
        </Fade>
      </StyledModal>
    </>
  );
};

export default function Services() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(rgba(14, 84, 109, 0.1), rgba(22, 178, 139, 0.1)), url(${servicesBg})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        paddingTop: '120px',
        paddingBottom: '80px',
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Fade in={true} timeout={1000}>
          <Box textAlign="center">
            <Typography
              variant="h2"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.primary,
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.6,
                fontWeight: 400,
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '24px',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              Here at Constantia MediSuites, we pride ourselves on excellent service and medical treatment that build trust and keeps up to date with current medical research and health trends.
            </Typography>
          </Box>
        </Fade>
      </Container>

      {/* Services Grid */}
      <Container maxWidth="xl">
        <Grid container spacing={4} justifyContent="center">
          {doctors.map((doctor, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              xl={3}
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ width: '100%', maxWidth: 320 }}>
                <DoctorCard doctor={doctor} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Box
          sx={{
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '40px',
            borderRadius: '20px',
            backdropFilter: 'blur(15px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              mb: 2
            }}
          >
            Ready to Book an Appointment?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: 3,
              fontSize: '1.1rem'
            }}
          >
            Contact us today to schedule your consultation with any of our healthcare professionals.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<EmailOutlined />}
            href="mailto:reception@constantiamed.co.za"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: 'white',
              padding: '12px 32px',
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(22, 178, 139, 0.3)'
              }
            }}
          >
            Contact Reception
          </Button>
        </Box>
      </Container>
    </Box>
  );
}